import { useMutation, useQuery } from "@apollo/client";
import { NativeStackScreenProps } from "@react-navigation/native-stack";
import { Sale } from "@towersystems/roam-common/lib/generated-types";
import { Spacings, View } from "react-native-ui-lib";
import { SaleRootParamList, ValidSetupRootParamList } from "../../../../types";
import {
  Banner,
  useFlashMessages,
  useWithAppState,
} from "@towersystems/roam-common-native";
import { SaleState } from "../../../../utilities/data/model/Sale";
import { moneyToFormattedString } from "../../../../utilities/money";
import { useSwitchSale } from "../../../point-of-sale/utils/sale-controller";
import { SaleCustomerSummary } from "../../components/SaleCustomerSummary";
import { SaleLineItemsSummary } from "../../components/SaleLineItemsSummary";
import { SalePaymentsSummary } from "../../components/SalePaymentsSummary";
import { SaleResourceListItem } from "../../components/SaleResourceListItem";
import {
  QUERY_SALE_LIST,
  useCancelSale,
  useQuerySale,
  useRefundSale,
  useTransferSale,
  useVoidSale,
} from "../../utils";
import {
  Badge,
  Card,
  Divider,
  Page,
  Stack,
  Status,
  Text,
  Button,
  TileButtonActionList,
} from "@towersystems/roam-common-native";
import { SaleTotalsSummary } from "../../components/SaleTotalsSummary";
import { ResourcePageSkeleton } from "../../../point-of-sale/screens/ResourcPageSkeleton";
import { useTranslation } from "../../../../utilities/i18n";
import { WebWarningBanner } from "../../components/WebWarningBanner";
import { LocationWarningBanner } from "../../components/LocationWarningBanner";
import {
  formatDateToCalendar,
  fullDateTimeFormat,
} from "@towersystems/roam-common/lib/shared-utils";
import React from "react";
import { MUTATION_ACTIVATE_SALE_GIFT_CARDS } from "../../../../utilities/gift-card/constants";
import { REDEEM_OUTSTANDING_GIFT_VOUCHERS } from "../../../../utilities/gift-voucher";
import { SaleLaybySummary } from "../../components/SaleLaybySummary";
import { FlatList } from "react-native";
import { useLlmQuickWinsProvider } from "../../../../utilities/llm-quick-wins";

export type SaleScreenNavigationProp = NativeStackScreenProps<
  SaleRootParamList & ValidSetupRootParamList,
  "SaleScreen"
>;

export interface SaleScreenProps extends SaleScreenNavigationProp {}

export const SaleScreen = ({ route, navigation }: SaleScreenProps) => {
  const { t } = useTranslation("sales");
  const saleId = route.params.saleId;
  const { item, loading, refetch } = useQuerySale({ saleId });

  const { refundSale } = useRefundSale();
  const { voidSale } = useVoidSale();
  const { cancelSale } = useCancelSale();
  const { appState } = useWithAppState();
  const { transferWebSale } = useTransferSale();
  const flashMessagesContext = useFlashMessages();
  const { strategyEnabled } = useLlmQuickWinsProvider();

  const { data: refundsData } = useQuery<{
    sales: { items: Sale[] };
  }>(QUERY_SALE_LIST, {
    variables: { options: { filter: { returnForId: { eq: saleId } } } },
  });

  const [mutationActivateSaleGiftCards] = useMutation<{
    activateSaleGiftCards: Sale;
  }>(MUTATION_ACTIVATE_SALE_GIFT_CARDS);

  const [mutationRedeemOutstandingGiftVouchers] = useMutation<{
    redeemOutstandingSaleGiftVouchers: {
      id: string;
    };
  }>(REDEEM_OUTSTANDING_GIFT_VOUCHERS);

  const locationBasedActionsDisabled =
    !appState.locationId || !item || item.locationId !== appState.locationId;

  const switchSale = useSwitchSale();

  const handleOnActivingGiftCard = React.useCallback(
    (saleId: string) => {
      mutationActivateSaleGiftCards({
        variables: {
          saleId,
        },
      })
        .then((result) => {
          if (result.data?.activateSaleGiftCards) {
            console.log(result.data.activateSaleGiftCards);
            flashMessagesContext.showMessage({
              message: "Gift card activated successfully",
              severity: "success",
            });
            return refetch();
          }
        })
        .catch((e) => {
          flashMessagesContext.showMessage({
            message: "Failed to activate gift card",
            severity: "error",
          });
        });
    },
    [mutationActivateSaleGiftCards, refetch],
  );

  const handleOnActivateGiftVouchers = React.useCallback(
    (saleId: string) => {
      mutationRedeemOutstandingGiftVouchers({
        variables: {
          saleId,
        },
      })
        .then((result) => {
          if (result.data?.redeemOutstandingSaleGiftVouchers) {
            flashMessagesContext.showMessage({
              message: "Gift vouchers redeemed successfully",
              severity: "success",
            });
            return refetch();
          }
        })
        .catch((e) => {
          flashMessagesContext.showMessage({
            message: "Failed to redeem gift vouchers",
            severity: "error",
          });
        });
    },
    [mutationActivateSaleGiftCards, refetch],
  );

  if (!loading && !item) return <Text>Not found</Text>;

  if (loading || !item) {
    return <ResourcePageSkeleton />;
  }

  const isCartSale =
    item.state === SaleState.SAVED && item.source == "WEB_CHECKOUT";

  let title = isCartSale
    ? t("view_cart_title")
    : t("view_sale_title", { number: item?.invoiceNumber ?? "" });

  if (item.saleType === "QUOTE") {
    title = t("view_quote_title", { number: item?.invoiceNumber ?? "" });
  }

  if (item.saleType === "INVOICE") {
    title = t("view_invoice_title", { number: item?.invoiceNumber ?? "" });
  }

  if (item.saleType === "LAYBY") {
    title = t("view_layby_title", { number: item?.invoiceNumber ?? "" });
  }

  if (item.saleType === "PROFORMA") {
    title = t("view_proforma_title", { number: item?.invoiceNumber ?? "" });
  }

  let actions = [];

  const handleTransferSale = () => {
    transferWebSale(item.id)
      .then(async (i) => {
        return switchSale(i).then(() =>
          navigation.navigate("RegisterRootScreen", {
            screen: "SellScreen",
          }),
        );
      })
      .catch((e) => console.log(e));
  };

  if (item.source === "WEB_REGISTER") {
    if (
      [
        SaleState.SAVED,
        SaleState.LAYBY_SAVED,
        SaleState.QUOTE_SAVED,
        SaleState.INVOICE_SAVED,
        SaleState.OPEN,
        SaleState.LAYBY_OPEN,
        SaleState.PROFORMA_SAVED,
      ].includes(item.state as SaleState)
    ) {
      actions.push({
        isAction: true,
        id: 1,
        disabled:
          locationBasedActionsDisabled || item.id == appState.localSaleId,
        label: t("action.continue_sale.label"),
        onPress: async () => {
          try {
            await switchSale(item);
            navigation.navigate("RegisterRootScreen", {
              screen: "SellScreen",
            });
          } catch (err) {
            console.log(err);
          }
        },
      });
    }

    if (
      [
        SaleState.LAYBY_CLOSED,
        SaleState.CLOSED,
        SaleState.INVOICE_CLOSED,
        SaleState.PROFORMA_CLOSED,
        SaleState.QUOTE_CLOSED,
      ].includes(item.state as SaleState)
    ) {
      actions.push({
        isAction: true,
        id: 3,
        label: t("action.receipts.label"),
        onPress: () => {
          navigation.navigate("ReceiptsModalRootScreen", {
            saleId: item.id,
          });
        },
      });
    }

    if (
      [
        SaleState.LAYBY_CLOSED,
        SaleState.CLOSED,
        SaleState.INVOICE_CLOSED,
        SaleState.QUOTE_CLOSED,
        SaleState.PROFORMA_CLOSED,
      ].includes(item.state as SaleState)
    ) {
      actions.push({
        isAction: true,
        id: 5,
        label: t("action.export.label"),
        onPress: () => {
          navigation.navigate("ExportModalRootScreen", { saleId: item.id });
        },
      });
    }
    if (
      [SaleState.CLOSED].includes(item.state as SaleState) &&
      false == item.isReturn
    ) {
      actions.push({
        isAction: true,
        id: 2,
        label: t("action.refund.label"),
        disabled: locationBasedActionsDisabled,
        onPress: async () => {
          return refundSale(item.id)
            .then(async (sale: Sale) => {
              await switchSale(sale);
              navigation.navigate("RegisterRootScreen", {
                screen: "SellScreen",
              });
            })
            .catch((e) => console.log(e));
        },
      });
    }

    if ([SaleState.CLOSED].includes(item.state as SaleState)) {
      actions.push({
        isDestructive: true,
        id: 22,
        label: t("action.void.label"),
        onPress: async () => {
          return voidSale(item.id)
            .then((i) => {
              return refetch(i);
            })
            .catch((e) => console.log(e));
        },
      });
    }

    if (
      [
        SaleState.SAVED,
        SaleState.LAYBY_SAVED,
        SaleState.QUOTE_SAVED,
        SaleState.INVOICE_SAVED,
      ].includes(item.state as SaleState)
    ) {
      actions.push({
        isDestructive: true,
        id: 13,
        disabled: false,
        label: t("action.cancel.label"),
        onPress: async () => {
          return cancelSale(item.id)
            .then((i) => {
              return refetch(i);
            })
            .catch((e) => console.log(e));
        },
      });
    }
  }

  if (item.source === "WEB_CHECKOUT") {
    if ([SaleState.SAVED].includes(item.state as SaleState)) {
      actions.push({
        isAction: true,
        id: 50,
        label: t("action.web_transfer.label"),
        onPress: handleTransferSale,
        disabled: locationBasedActionsDisabled,
      });
    }

    if ([SaleState.CLOSED].includes(item.state as SaleState)) {
      actions.push({
        isAction: true,
        id: 3,
        label: t("action.receipts.label"),
        onPress: () => {
          navigation.navigate("ReceiptsModalRootScreen", {
            saleId: item.id,
          });
        },
      });
    }

    if ([SaleState.CLOSED].includes(item.state as SaleState)) {
      actions.push({
        isAction: true,
        id: 5,
        label: t("action.export.label"),
        onPress: () => {
          navigation.navigate("ExportModalRootScreen", { saleId: item.id });
        },
      });
    }
  }

  if (item.state.includes("PROFORMA")) {
    actions.push({
      isAction: true,
      id: 6,
      label: t("action.view_proforma.label"),
      onPress: async () => {
        console.log(item.id);
        return navigation.navigate("ViewProformaScreen", {
          saleId: item.id,
        });
      },
    });
  }

  const webCheckoutWarning =
    item.source === "WEB_CHECKOUT" ? <WebWarningBanner /> : undefined;

  const locationBasedActionsDisabledWarningMarkup =
    locationBasedActionsDisabled ? (
      <LocationWarningBanner name={item.location?.name} />
    ) : undefined;

  const returnsMarkup =
    refundsData?.sales.items && refundsData.sales.items.length >= 1 ? (
      <Card title={t("card.refunds.title")} style={{ marginTop: Spacings.s4 }}>
        <FlatList
          ItemSeparatorComponent={Divider}
          renderItem={({ item }) => <SaleResourceListItem sale={item} />}
          data={refundsData.sales.items}
        />
      </Card>
    ) : undefined;

  const footer =
    item.isReturn && item.returnForId ? (
      <View>
        <Button
          isAction
          label={t("button.go_original_sale")}
          onPress={() =>
            // @ts-ignore
            navigation.navigate("SaleScreen", { saleId: item.returnForId })
          }
        ></Button>
      </View>
    ) : undefined;

  const secondaryTitle = (
    <View>
      <Text fontWeight="bold" variant="heading2">
        {moneyToFormattedString(item.totalPrice)}
      </Text>
      {item.source === "WEB_REGISTER" ? (
        item.state == "TRANSFERRED" ? (
          <Text variant="heading2">
            {t("transferred_at", {
              time: formatDateToCalendar(item.saleDate, fullDateTimeFormat),
              location: item.location?.name ?? t("common:unknown_location"),
              name: item.staff?.fullName ?? "Owner",
            })}
          </Text>
        ) : item.location ? (
          <Text variant="heading2">
            {t(item.saleType === "SALE" ? "sold_by_at" : "by_at", {
              time: formatDateToCalendar(item.saleDate, fullDateTimeFormat),
              location: item.location?.name ?? t("common:unknown_location"),
              name: item.staff?.fullName ?? "Owner",
            })}
          </Text>
        ) : undefined
      ) : (
        item.location && (
          <Text variant="heading2">
            {t("sold_by", {
              time: formatDateToCalendar(item.saleDate, fullDateTimeFormat),
              location: item.location?.name ?? t("common:unknown_location"),
            })}
          </Text>
        )
      )}
    </View>
  );

  const showNonActivatedGiftCardWarning = item.lineItems.some(
    (lineItem) =>
      lineItem.giftCardNumber && !lineItem.metadata?.giftVoucherActivatedDate,
  );

  const showNonActivatedVouchersWarning =
    item.giftVoucherCode && !item.giftVoucherRedemptionDate;

  const showNonActivatedVouchersWarningMarkup =
    showNonActivatedVouchersWarning ? (
      <Banner
        onPress={() => {
          return handleOnActivateGiftVouchers(saleId);
        }}
        status="warning"
        title={t("banner.non_redeemed_gift_vouchers.title")}
      >
        <Text>{t("banner.non_activated_gift_card.description")}</Text>
      </Banner>
    ) : undefined;

  const nonActivatedGiftVoucherWarningMarkup =
    showNonActivatedGiftCardWarning ? (
      <Banner
        onPress={() => {
          return handleOnActivingGiftCard(saleId);
        }}
        status="warning"
        title={t("banner.non_activated_gift_card.title")}
      >
        <Text>{t("banner.non_activated_gift_card.description")}</Text>
      </Banner>
    ) : undefined;

  const purchaseOrderNumber =
    item.purchaseOrderNumber &&
    item.purchaseOrderNumber !== item.invoiceSequence?.toString()
      ? ` / PO#${item.purchaseOrderNumber}`
      : null;

  const normalizedTitle = purchaseOrderNumber
    ? `${title} - ${purchaseOrderNumber}`
    : title;

  return (
    <Page
      title={normalizedTitle}
      footer={footer}
      badges={
        <Stack spacing={Spacings.s2}>
          <Badge status={item.state as Status}>{item.state}</Badge>
          <Badge status={item.source as any}>{item.source}</Badge>
          {item.isReturn ? <Badge>REFUND</Badge> : undefined}
        </Stack>
      }
      secondaryTitle={secondaryTitle}
    >
      <Stack vertical>
        {nonActivatedGiftVoucherWarningMarkup}
        {showNonActivatedVouchersWarningMarkup}
        {webCheckoutWarning}
        {locationBasedActionsDisabledWarningMarkup}
        {actions.length >= 1 ? (
          <View>
            <TileButtonActionList actions={actions} />
          </View>
        ) : null}
        <Divider />
        {item.customer ? (
          <Card title={t("card.customer.title")} filled>
            <SaleCustomerSummary customer={item.customer} />
          </Card>
        ) : undefined}
        {item.saleType === "LAYBY" ? (
          <Card title={t("card.layby.title")} filled>
            {<SaleLaybySummary sale={item} />}
          </Card>
        ) : undefined}
        {item.lineItems.length >= 1 ? (
          <Card title={t("card.items.title")} filled>
            <View>
              <SaleLineItemsSummary
                onItemPress={
                  strategyEnabled("product_summary_quick_win")
                    ? (item) => {
                        navigation.navigate(
                          "ProductAiInsightsRootModalScreen",
                          {
                            screen: "ProductAiInsightsScreen",
                            productId: item.productId!,
                          },
                        );
                      }
                    : undefined
                }
                sale={item}
              />
            </View>
          </Card>
        ) : undefined}
        <Card title={t("card.totals.title")} filled>
          <SaleTotalsSummary sale={item} />
        </Card>
        {item.payments.length >= 1 ? (
          <Card title={t("card.payments.title")} filled>
            <View>
              <SalePaymentsSummary sale={item} />
            </View>
          </Card>
        ) : undefined}
        {returnsMarkup}
      </Stack>
    </Page>
  );
};
